import React from 'react';
import Header from './Header';
import background from './img/softwarePartners.png';
import MobileNavigation from './MobileNavigation';
import PropTypes from 'prop-types';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { CopyBlock, dracula } from 'react-code-blocks';
import Footer from './Footer';

const StyledTabs = styled((props) => (
  <Tabs
    {...props}
    TabIndicatorProps={{
      children: <span className="MuiTabs-indicatorSpan" />
    }}
    variant="scrollable"
    scrollButtons
    allowScrollButtonsMobile
    centered
  />
))(({ theme }) => ({
  '& .MuiTabs-indicator': {
    display: 'flex',
    justifyContent: 'center',
    backgroundColor: 'transparent'
  },
  '& .MuiTabs-indicatorSpan': {
    width: '100%',
    backgroundColor: '#1C7BC0'
  }
}));

const StyledTab = styled((props) => <Tab disableRipple {...props} />)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: theme.typography.fontWeightRegular,
  fontSize: theme.typography.pxToRem(15),
  marginRight: theme.spacing(1),
  color: '#000000',
  '&.Mui-selected': {
    color: '#000000',
    fontWeight: theme.typography.fontWeightMedium
  },
  '&.Mui-focusVisible': {
    backgroundColor: 'rgba(100, 95, 228, 0.32)'
  }
}));

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <Typography component="span">{children}</Typography>
        </Box>
      )}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired
};

function TabProps(index) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`
  };
}

const DeveloperTokenizer = () => {
  const [value, setValue] = useState(0);
  const handleChange = (event, newValue) => {
    setValue(newValue);
  };
  return (
    <>
      <MobileNavigation />
      <section className="bg-[#212121]">
        <div
          className="w-full pb-28"
          style={{
            backgroundImage: `url(${background})`,
            backgroundSize: '200% 200%',
            backgroundPosition: 'bottom center',
            backgroundRepeat: 'no-repeat'
          }}
        >
          <Header />
          <div className="p-4 row md:p-8 how">
            <h3 className="text-white large">Tokenization</h3>
          </div>
          <div className="flex p-16 row flex-col justify-center flex-start gap-6 self-stretch rounded-[28px] bg-white !mb-10">
            <p className="text-black light">
              The purpose of the Tokenizer API is to provide a javascript implementation that
              injects hosted payment fields onto your website. By injecting the hosted payment page
              into an iframe via this library, a client remains outside the scope of PCI while still
              having full control over the processing of the transaction. Once the sensitive data
              has been collected and tokenized, you may submit any regular API call exchanging the
              sensitive information with the token you have received within 2 minutes.
            </p>

            <div className="my-4 callout">
                  <p className="mb-0 text-black light">
                    <strong>Note:</strong> All of the API and code examples in this documentation are all in the sandbox environment. The scope URI is <a href="https://sandbox.account.basyspro.com/c7d093fb-b130-44a9-a733-529551341975/.default" className="links" target="_blank" rel="noopener noreferrer">https://sandbox.account.basyspro.com/c7d093fb-b130-44a9-a733-529551341975/.default</a>.
                  </p>
                </div>


            <p className="text-black light">
              See the following pages on how to embed the Basys tokenizer into your workflows:
            </p>
            {/* TABS */}
            <Box sx={{ width: '100%' }}>
              <Box
                display="flex"
                width="100%"
                sx={{ borderBottom: 1, borderColor: 'divider', justifyContent: 'center' }}
              >
                <StyledTabs value={value} onChange={handleChange}>
                  <StyledTab label="Card Tokenization & Transaction Flow" {...TabProps(0)} />
                  <StyledTab label="ACH Tokenization & Transaction Flow" {...TabProps(1)} />
                </StyledTabs>
              </Box>
              <TabPanel value={value} index={0}>
                <h2 className="text-black large">Card Tokenization & Transaction Flow</h2>

                <p className="text-black light" style={{ textWrap: 'wrap', textWrapMode: 'auto' }}>
                  This document outlines the setup and processing flow required to support card
                  transactions using the iFrame and API integration.
                </p>

                <div className="my-6 separator" />

                <h2 className="mt-8 text-black large">1. App Registration & Authentication</h2>

                <h3 className="mt-4 text-2xl font-medium text-black">1.1 Create an Application</h3>

                <p className="text-black light">
                  In the iQ Pro+ UI: <strong>Developer Hub → Applications → Create New +</strong>
                </p>

                <ul className="ml-6 list-disc">
                  <li className="text-black light">Role: Integrated Service Account</li>
                  <li className="text-black light">Record Client ID and Client Secret</li>
                </ul>

                <h3 className="mt-4 text-2xl font-medium text-black">1.2 Obtain Bearer Token</h3>

                <CopyBlock
                  text={`POST https://sandbox.account.basyspro.com/account.basyspro.com/B2C_1A_IQPROV2_INTEGRATOR/oauth2/v2.0/token

Headers:
Content-Type: application/x-www-form-urlencoded

Body:
grant_type=client_credentials
client_id={CLIENT_ID}
client_secret={CLIENT_SECRET}
scope=https://account.basyspro.com/{sandbox-tenant-id}/.default`}
                  language="bash"
                  theme={dracula}
                />
                <ul className="mt-4 ml-6 list-disc">
                  <li className="text-black light">
                    Sandbox Tenant ID: <code>c7d093fb-b130-44a9-a733-529551341975</code>
                  </li>
                </ul>
                <p className="text-black light">
                  <b>Response</b>
                </p>
                <p className="text-black light">
                  A successful request returns an <code>access_token</code>.
                </p>
                <p className="text-black light">
                  <b>Usage</b>
                </p>
                <CopyBlock text={`Authorization: Bearer {TOKEN}`} language="bash" theme={dracula} />

                <p className="mt-4 text-black light">
                  Use this token for all subsequent API requests.
                </p>

                <div className="my-6 separator" />

                <h3 className="mt-4 text-2xl font-medium text-black">
                  2. Retrieve iFrame Configuration
                </h3>

                <CopyBlock
                  text={`GET https://sandbox.api.basyspro.com/iqsaas/v1/api/v1/Gateway/{gatewayId}/Tokenization/Configuration

Headers:
Authorization: Bearer {TOKEN}
Accept: application/json`}
                  language="bash"
                  theme={dracula}
                />

                <p className="text-black light">
                  <b>Required Fields Mapping</b>
                </p>

                <div className="my-4 overflow-x-auto">
                  <table className="w-full text-black tg">
                    <thead>
                      <tr>
                        <th className="text-left">Field</th>
                        <th className="text-left">Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>tokenExID</td>
                        <td>
                          <code>data.iframeConfiguration.iqProV2.tokenizationId</code>
                        </td>
                      </tr>
                      <tr>
                        <td>tokenScheme</td>
                        <td>
                          <code>data.iframeConfiguration.iqProV2.tokenScheme</code>
                        </td>
                      </tr>
                      <tr>
                        <td>authenticationKey</td>
                        <td>
                          <code>data.iframeConfiguration.iqProV2.authenticationKey</code>
                        </td>
                      </tr>
                      <tr>
                        <td>timestamp</td>
                        <td>
                          <code>data.iframeConfiguration.iqProV2.timestamp</code> (format:
                          yyyyMMddHHmmss)
                        </td>
                      </tr>
                      <tr>
                        <td>origin</td>
                        <td>
                          <code>data.iframeConfiguration.iqProV2.origin</code>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="my-4 callout">
                  <p className="mb-0 text-black light">
                    <strong>Note:</strong> The <code>timestamp</code> must match the value used to
                    generate the <code>authenticationKey</code>.
                  </p>
                </div>

                <div className="my-6 separator" />

                <h2 className="mt-8 text-black large">3. iFrame Card Flow</h2>

                <h3 className="mt-4 text-2xl font-medium text-black">3.1 Load Script</h3>

                <CopyBlock
                  text={`<script src="https://sandbox.api.basyspro.com/Iframe/iframe/iframe-v3.js"></script>`}
                  language="html"
                  theme={dracula}
                />

                <h3 className="mt-4 text-2xl font-medium text-black">3.2 Initialize iFrame</h3>

                <CopyBlock
                  text={`<div id="tokenExIframeDiv"></div>
      
<script>
  const iframeConfig = {
    tokenExID: "<from config API>",
    tokenScheme: "<from config API>",
    authenticationKey: "<from config API>",
    timestamp: "<from config API>", // yyyyMMddHHmmss
    origin: window.location.origin  // must match configured origin
  };
  const iframe = new TokenEx.Iframe("tokenExIframeDiv", iframeConfig);
  // Optional event handlers: onLoad, onError, onTokenize
  iframe.load();
</script>`}
                  language="html"
                  theme={dracula}
                />

                <h4 className="mt-6 text-black">3.3 Submit for Tokenization</h4>

                <CopyBlock
                  text={`<script>
  document.getElementById('tokenExIframeDiv').addEventListener('submit', (e) => {
    e.preventDefault();
    iframe.tokenize();
  });

  iframe.on('tokenize', function (data) {
    // Tokenized card value
    createTransaction(data.token);
  });
</script>`}
                  language="html"
                  theme={dracula}
                />

                <h3 className="mt-4 text-2xl font-medium text-black">3.4 Create the Transaction</h3>

                <CopyBlock
                  text={`POST /transaction

Headers:
Authorization: Bearer {TOKEN}
Content-Type: application/json

Body Example:
{
  "amount": 25.00,
  "currency": "USD",
  "transactionType": "SALE",
  "paymentMethod": {
    "card": {
      "cardToken": "{{TOKEN_FROM_IFRAME}}",
      "cardType": "Visa",
      "expirationDate": null,
      "maskedNumber": "************1111"
    }
  }
}`}
                  language="json"
                  theme={dracula}
                />

                <p className="mt-4 text-black light">
                  Response returns transaction result (approved/declined) and transactionId.
                </p>
              </TabPanel>
              <TabPanel value={value} index={1}>
                <h2 className="text-black large">ACH Tokenization & Transaction Flow</h2>

                <p className="text-black light" style={{ textWrap: 'wrap', textWrapMode: 'auto' }}>
                  This section outlines the setup and processing flow required to support ACH
                  transactions within your integration. This requires direct API tokenization.
                </p>

                <div className="my-6 separator" />

                <h2 className="mt-8 text-black large">1. Rendering ACH Fields in Your iFrame</h2>
                <p className="text-black light">
                  To enable ACH transactions, enable the following input fields:
                </p>
                <ul className="ml-6 list-disc">
                  <li className="text-black light">routingNumber</li>
                  <li className="text-black light">accountNumber</li>
                </ul>
                <p className="text-black light">
                  <b>Example Markup</b>
                </p>
                <CopyBlock
                  text={`<div class="payment-bank-section">
  <div class="info-items">
    <label for="achAccountType">Account Type</label>
    <select id="achAccountType" name="achAccountType">
      <option value="Checking">Checking</option>
      <option value="Savings">Savings</option>
    </select>
  </div>
  <div class="info-items">
    <label for="routingNumber">Routing Number</label>
    <input 
      type="text"
      id="routingNumber"
      name="routingNumber"
      inputmode="numeric"
      autocomplete="off"
      maxlength="9"
      pattern="^\\d{9}$"
    />
  </div>
  <div class="info-items">
    <label for="accountNumber">Account Number</label>
    <input 
      type="text"
      id="accountNumber"
      name="accountNumber"
      inputmode="numeric"
      autocomplete="off"
      maxlength="17"
      pattern="^\\d{4,17}$"
    />
  </div>
</div>
`}
                  language="html"
                  theme={dracula}
                />

                <div className="my-6 separator" />

                <h2 className="mt-8 text-black large">2. Field Requirements</h2>

                <ul className="ml-6 list-disc">
                  <li className="text-black light">Routing Number must be 9 digits.</li>
                  <li className="text-black light">
                    Account Number must be between 4 and 17 digits.
                  </li>
                  <li className="text-black light">Account Type must be checking or savings.</li>
                </ul>

                <div className="my-6 separator" />

                <h2 className="mt-8 text-black large">3. Tokenizing ACH Data</h2>

                <p className="text-black light">
                  ACH tokenization requires a <strong>direct API call</strong> (not handled by the
                  iFrame).
                </p>
                <p className="text-black light">
                  <b>Flow</b>
                </p>
                <ul className="ml-6 list-disc">
                  <li className="text-black light">Capture ACH field values</li>
                  <li className="text-black light">Call ACH Tokenization endpoint</li>
                  <li className="text-black light">
                    Receive <code>achToken</code>
                  </li>
                  <li className="text-black light">Use token in transaction request</li>
                </ul>

                <div className="my-6 separator" />

                <h2 className="mt-8 text-black large">4. ACH Tokenization API</h2>

                <CopyBlock
                  text={`POST https://sandbox.api.basyspro.com/vault/api/v1/Tokenize/Ach

Headers:
Authorization: Bearer {TOKEN}
Content-Type: application/json

Body Example:
{
  "accountNumber": "123456789",
  "routingNumber": "021000021",
  "secCode": "WEB",
  "achAccountType": "Checking"
}`}
                  language="json"
                  theme={dracula}
                />

                <p className="text-black light">
                  <b>Field Descriptions</b>
                </p>

                <div className="my-4 overflow-x-auto">
                  <table className="w-full text-black tg">
                    <thead>
                      <tr>
                        <th className="text-left">Field</th>
                        <th className="text-left">Type</th>
                        <th className="text-left">Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>accountNumber</td>
                        <td>string</td>
                        <td>Customer’s bank account number</td>
                      </tr>
                      <tr>
                        <td>routingNumber</td>
                        <td>string</td>
                        <td>9-digit routing number</td>
                      </tr>
                      <tr>
                        <td>secCode</td>
                        <td>string</td>
                        <td>
                          Standard Entry Class (<code>WEB</code>, <code>PPD</code>, <code>CCD</code>
                          , etc.)
                        </td>
                      </tr>
                      <tr>
                        <td>achAccountType</td>
                        <td>string</td>
                        <td>Checking or Savings</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="my-6 separator" />

                <h2 className="mt-8 text-black large">5. Processing the ACH Transaction</h2>

                <p className="text-black light">
                  Once the <code>achToken</code> is returned, include it in your transaction
                  request.
                </p>

                <p className="text-black light">
                  <b>Example ACH Object</b>
                </p>

                <CopyBlock
                  text={`{
  "ach": {
    "achToken": "returned_ach_token",
    "secCode": "WEB",
    "routingNumber": "021000021",
    "accountType": "Checking",
    "checkNumber": null,
    "accountHolderAuth": {
      "dlState": null,
      "dlNumber": null
    }
  }
}`}
                  language="json"
                  theme={dracula}
                />

                <p className="text-black light">
                  <b>Field Notes</b>
                </p>

                <ul className="ml-6 list-disc">
                  <li className="text-black light">
                    <b>achToken</b> — Returned from tokenization API
                  </li>
                  <li className="text-black light">
                    <b>secCode</b> — Defaults to <code>WEB</code>
                  </li>
                  <li className="text-black light">
                    <b>accountType</b> — Defaults to <code>Checking</code>
                  </li>
                  <li className="text-black light">
                    <b>checkNumber</b> — Typically <code>null</code>
                  </li>
                  <li className="text-black light">
                    <b>accountHolderAuth</b> — Optional
                  </li>
                </ul>

                <div className="my-6 separator" />

                <h2 className="mt-8 text-black large">6. End-to-End Flow Summary</h2>

                <ul className="ml-6 list-disc">
                  <li className="text-black light">Render ACH fields</li>
                  <li className="text-black light">User submits form</li>
                  <li className="text-black light">Call ACH tokenization API</li>
                  <li className="text-black light">
                    Receive <code>achToken</code>
                  </li>
                  <li className="text-black light">
                    Submit transaction with <code>achToken</code>
                  </li>
                </ul>
              </TabPanel>
            </Box>
          </div>
        </div>
      </section>
      <Footer />
    </>
  );
};

export default DeveloperTokenizer;
